/**
 * Scoring Guide Modal for Personal Gap Analysis
 * Displays scoring guide with ranges and explanations
 */
import React from 'react';
import PropTypes from 'prop-types';
import { X, ChevronDown, ChevronRight } from 'lucide-react';

const ScoringGuideModal = ({
  show,
  onClose,
  isCollapsed,
  onToggleCollapse
}) => {
  if (!show) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-2 sm:p-4">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex items-center justify-between">
          <h3 className="text-xl font-bold text-gray-900">Scoring Guide</h3>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Collapsible Scoring Image */}
          <div className="mb-4">
            <button
              onClick={onToggleCollapse}
              className="flex items-center justify-between w-full p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
            >
              <span className="font-semibold text-gray-900">Scoring Reference Chart</span>
              {isCollapsed ? <ChevronRight size={20} /> : <ChevronDown size={20} />}
            </button>

            {!isCollapsed && (
              <div className="mt-3 p-4 bg-gray-50 rounded-lg">
                <img
                  src="/scoring-guide.png"
                  alt="Scoring Guide"
                  className="w-full h-auto rounded"
                />
              </div>
            )}
          </div>

          {/* Scoring Descriptions */}
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 mb-2">How Scores Are Calculated</h4>
              <p className="text-sm text-gray-600">
                Each question is scored on a scale that reflects your readiness in that area of personal and exit planning.
                The overall score is calculated by averaging all category scores, giving you a comprehensive
                view of your financial, relational, and spiritual preparedness for exit.
              </p>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Score Ranges</h4>
              <p className="text-sm text-gray-600 mb-3">Based on the assessment scoring scale where average readiness is around 50%:</p>
              <div className="space-y-2">
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#c49e73' }}></div>
                  <span className="text-sm"><strong>86-100% (No Gaps):</strong> Exceptional readiness, fully prepared</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#a7d5a8' }}></div>
                  <span className="text-sm"><strong>72-86% (Minor Gaps):</strong> Strong readiness, minor improvements possible</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#b8d4e8' }}></div>
                  <span className="text-sm"><strong>57-72% (Considerable Gaps):</strong> Average readiness, clear room for improvement</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f4ebb0' }}></div>
                  <span className="text-sm"><strong>43-57% (Critical Gaps):</strong> Below average, significant work needed</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f5d7b3' }}></div>
                  <span className="text-sm"><strong>28-43% (Very Critical Gaps):</strong> Poor readiness, urgent attention required</span>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="w-4 h-4 rounded" style={{ backgroundColor: '#f5c9c9' }}></div>
                  <span className="text-sm"><strong>0-28% (Extremely Critical):</strong> Critical deficiency or not applicable</span>
                </div>
              </div>
              <div className="mt-3 p-3 bg-blue-50 border-l-4 border-blue-500 rounded">
                <p className="text-sm text-gray-700">
                  <strong>Note:</strong> A score of 67% is the "green zone" target representing solid preparedness. A score of 72% or above is considered "best in class," representing clear planning, documented strategies, and comprehensive readiness across all domains.
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-gray-900 mb-2">Using Your Results</h4>
              <p className="text-sm text-gray-600 mb-2">
                Focus on categories with lower scores first. Click on any category to expand and see
                individual questions. Use the gap distribution chart to filter questions by score range
                and prioritize your planning efforts.
              </p>
              <p className="text-sm text-gray-600">
                Remember: A lower score isn't bad—it's an opportunity. It shows you exactly where to focus
                your efforts to ensure a successful transition and fulfilling life after exit.
              </p>
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="sticky bottom-0 bg-white border-t border-gray-200 p-4">
          <button
            onClick={onClose}
            className="w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition min-h-[44px]"
          >
            Got it
          </button>
        </div>
      </div>
    </div>
  );
};

ScoringGuideModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  isCollapsed: PropTypes.bool.isRequired,
  onToggleCollapse: PropTypes.func.isRequired
};

export default ScoringGuideModal;
